// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The slice of Web Audio this product actually uses.
//
// Structural interfaces rather than the DOM's own types, for the reason `artProgram.ts` takes a
// structural `Canvas2D`: a real `AudioContext` satisfies these, and so does a two-hundred-line
// recording stub, which is what lets the mixer, the voice pool and every synthesis recipe be
// tested in node. Audio is otherwise the least testable layer in the product — you cannot assert
// on a sound — so the parts that can be checked have to be reachable without a browser, or
// nothing is checked at all.
//
// Deliberately small. Every member here is one the product calls; adding the rest of the API
// "for completeness" would make the stub a re-implementation of Web Audio rather than a
// recording of what we asked it for.

export interface AudioParamLike {
  value: number
  setValueAtTime: (value: number, startTime: number) => AudioParamLike
  linearRampToValueAtTime: (value: number, endTime: number) => AudioParamLike
  /**
   * Exponential approach, which is what every ducking and envelope move here uses.
   *
   * `linearRamp` needs an end time, so a duck written with it has to know when it finishes.
   * `setTargetAtTime` needs only a time constant, so a duck can be re-aimed mid-move — which is
   * what actually happens when a second callout starts while the first is still releasing.
   */
  setTargetAtTime: (target: number, startTime: number, timeConstant: number) => AudioParamLike
  cancelScheduledValues: (startTime: number) => AudioParamLike
}

export interface AudioNodeLike {
  connect: (destination: AudioNodeLike) => AudioNodeLike
  disconnect: () => void
}

export interface GainNodeLike extends AudioNodeLike {
  readonly gain: AudioParamLike
}

export interface CompressorNodeLike extends AudioNodeLike {
  readonly threshold: AudioParamLike
  readonly knee: AudioParamLike
  readonly ratio: AudioParamLike
  readonly attack: AudioParamLike
  readonly release: AudioParamLike
  /** Gain reduction currently applied, dB and negative. Web Audio's own meter; see `Mixer.limiting`. */
  readonly reduction: number
}

/** A rendered one-shot, on the GPU side of the fence. */
export interface AudioBufferLike {
  readonly duration: number
  readonly length: number
  readonly sampleRate: number
  readonly numberOfChannels: number
  getChannelData: (channel: number) => Float32Array
}

export interface BufferSourceLike extends AudioNodeLike {
  buffer: AudioBufferLike | null
  readonly playbackRate: AudioParamLike
  /**
   * Play the buffer end to end, forever. The rolling bed (M3-24), and nothing else.
   *
   * A one-shot and a loop are the same node with this flag flipped, which is the reason the
   * rolling layer needs no second playback mechanism: one buffer of pink noise, looping, with
   * its rate and its filter moved by the ball's own speed.
   */
  loop: boolean
  onended: (() => void) | null
  start: (when?: number) => void
  stop: (when?: number) => void
}

/**
 * A runtime filter.
 *
 * The one place this product filters during playback rather than at load. 07 §1 rule 5 allows
 * it in as many words — "at runtime the cost is buffer playback **and filtering**" — and the
 * rolling bed is why the exception exists: the centre frequency tracks the ball's speed, so it
 * is a different filter sixty times a second and cannot be baked into anything.
 */
export interface BiquadFilterNodeLike extends AudioNodeLike {
  type: string
  readonly frequency: AudioParamLike
  readonly Q: AudioParamLike
}

/**
 * Equal-power stereo pan, −1 to 1.
 *
 * The flat-screen collapse of 07 §8: positional audio becomes a pan derived from playfield *x*.
 * A `PannerNode` with HRTF is M3-30 and is what the headset gets; on a laptop with its speakers
 * eight centimetres apart, HRTF costs a convolution per voice and buys nothing at all.
 */
export interface StereoPannerLike extends AudioNodeLike {
  readonly pan: AudioParamLike
}

/**
 * A source with a place in the room (M9-48, 07 §8).
 *
 * **The half `StereoPannerLike`'s own comment promised and nobody built.** It says HRTF *"is what
 * the headset gets"*; `space.ts`'s header says of the same seam *"this is the seam M5 replaces"*.
 * M5 shipped, M6, M7 and M8 shipped, and every sound in the browser — flat screen and headset
 * alike — went through a `StereoPannerNode`. So in WebXR the knocker inside the cabinet head, the
 * speech out of the backbox and a bumper at the far end of the playfield were all the same
 * distance away, at the same height, in the middle of the player's skull. The native client has
 * had `spatialBlend = 1` since M7-11.
 *
 * Positions are in **Web Audio's** frame — x right, y up, −z forward — and the caller converts
 * from the machine's, which is x right, y up the playfield, z above it. One conversion, at the
 * one place that knows both.
 */
export interface PannerNodeLike extends AudioNodeLike {
  panningModel: string
  distanceModel: string
  refDistance: number
  maxDistance: number
  rolloffFactor: number
  readonly positionX: AudioParamLike
  readonly positionY: AudioParamLike
  readonly positionZ: AudioParamLike
}

/**
 * Where the player's ears are, in the machine's own frame.
 *
 * **The listener moves rather than the machine**, which is the whole reason this is cheap: a
 * cabinet has a few dozen emitters and a player has one head, so posing the head into machine-local
 * coordinates once a frame costs one transform where posing every voice into the room would cost
 * one each. It also means a voice's position never has to be updated when the player carries the
 * machine across the room.
 */
export interface AudioListenerLike {
  readonly positionX: AudioParamLike
  readonly positionY: AudioParamLike
  readonly positionZ: AudioParamLike
  readonly forwardX: AudioParamLike
  readonly forwardY: AudioParamLike
  readonly forwardZ: AudioParamLike
  readonly upX: AudioParamLike
  readonly upY: AudioParamLike
  readonly upZ: AudioParamLike
}

/** What the mixer needs of a context. A real `AudioContext` is one of these. */
export interface AudioGraphContext {
  readonly currentTime: number
  readonly sampleRate: number
  readonly destination: AudioNodeLike
  createGain: () => GainNodeLike
  createDynamicsCompressor: () => CompressorNodeLike
  /**
   * Stop the clock (M3-55's pause).
   *
   * **Optional, because a stub is allowed not to have one and a paused stub is not a thing you
   * can hear anyway.** On a real context this also freezes `currentTime`, which is why pausing
   * needs nothing else: every caption deadline, envelope and duck in this product is scheduled
   * against that clock, so they all stop where they are and resume where they left off rather
   * than each keeping a private timer that has to be told.
   */
  suspend?: () => Promise<void>
  /** Start the clock again. Also what the first user gesture calls to make a context audible. */
  resume?: () => Promise<void>
}

/** Everything the runtime needs: the mixer's context, plus the means to play a buffer. */
export interface AudioPlaybackContext extends AudioGraphContext {
  createBuffer: (channels: number, length: number, sampleRate: number) => AudioBufferLike
  createBufferSource: () => BufferSourceLike
  createStereoPanner: () => StereoPannerLike
  createBiquadFilter: () => BiquadFilterNodeLike
  /**
   * Optional, and that is deliberate (M9-48).
   *
   * Every stub in the test suite implements this interface, and a required member would be a
   * member forty fixtures have to grow for a path none of them exercise — the audio parity
   * fixtures render flat, because a flat render is what can be compared to a spectrum. A context
   * without it is a context that never spatialises, which is exactly what a flat screen is.
   */
  createPanner?: () => PannerNodeLike
  /** Absent on a stub, and on the two browsers that never implemented the newer listener params. */
  readonly listener?: AudioListenerLike
}

/**
 * Reference distance for the rolloff, metres.
 *
 * A cabinet is about a metre and a half of playfield and the player stands at one end of it, so
 * one metre puts the reference roughly where the player's own end of the table is: everything on
 * the playfield is at or beyond it, and nothing is inside it where an inverse model climbs.
 */
export const HRTF_REFERENCE_M = 1

/** Past this a machine sound is at its floor, metres. Well outside any room a cabinet stands in. */
export const HRTF_MAX_M = 10

/**
 * Put a panner into the one configuration this product uses (M9-48).
 *
 * **One owner, because there are two callers** — a one-shot builds a panner per voice and a
 * continuous bed builds one per bed — and a rolling ball whose distance model disagreed with the
 * bumper it just hit would be the same machine heard two ways. `inverse` rather than `linear` or
 * `exponential`: linear barely separates the two ends of a table this size, and exponential
 * buries the top of it.
 */
export function asHrtf(panner: PannerNodeLike): PannerNodeLike {
  panner.panningModel = 'HRTF'
  panner.distanceModel = 'inverse'
  panner.refDistance = HRTF_REFERENCE_M
  panner.maxDistance = HRTF_MAX_M
  panner.rolloffFactor = 1
  return panner
}

/** Amplitude ratio from decibels. The mixing rules are all written in dB (07 §7). */
export function decibelsToGain(db: number): number {
  return Math.pow(10, db / 20)
}

/** Decibels from an amplitude ratio. Silence is −∞, reported as a large negative rather than that. */
export function gainToDecibels(gain: number): number {
  return gain <= 0 ? -120 : 20 * Math.log10(gain)
}
